import { PairMetric, pairMetricConfig } from "@trader-dashboard/shared";

type PairAnalysisTooltipProps = {
  active?: boolean;
  label?: string;
  payload?: Array<{ name: string; value: number; color: string }>;
  metric: PairMetric;
};

const formatTooltipDate = (dayKey: string): string => {
  const date = new Date(`${dayKey}T00:00:00`);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Recharts sends a row for every series, including pairs sitting at zero on
 * this day. Those are dropped so a hover shows only what was actually traded.
 */
export const PairAnalysisTooltip = ({
  active,
  label,
  payload,
  metric,
}: PairAnalysisTooltipProps): React.ReactNode => {
  if (!active || !payload || !label) return null;

  const rows = payload
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = rows.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-md">
      <p className="text-xs font-medium">{formatTooltipDate(label)}</p>
      {rows.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          No {pairMetricConfig[metric].label.toLowerCase()} orders
        </p>
      ) : (
        <>
          <div className="mt-1.5 grid gap-1">
            {rows.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2 text-xs">
                <span
                  className="h-1 w-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground">{entry.name}</span>
                <span className="ml-auto font-medium tabular-nums">{entry.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-1.5 flex items-center gap-2 border-t border-border pt-1.5 text-xs">
            <span className="text-muted-foreground">Total</span>
            <span className="ml-auto font-medium tabular-nums">{total}</span>
          </div>
        </>
      )}
    </div>
  );
};
